import React from 'react'
import type { ShareMinimalForSettingsModal } from './types'

// Components
import {
  Button,
  Flex,
  Breakpoint,
  LoadingPlaceholder,
  ErrorMessage,
  ModalInjectedProps,
} from '@sketch/components'

// Styles
import {
  StyledModal as Modal,
  StyledBody as ModalBody,
  StyledSeparator,
  SharingModalHeader,
  StyledBreadcrumbs as Breadcrumbs,
} from './ShareSettings.styles'

import { CopyButton } from './PublicPermissions/PublicPermissions.styles'

// Content
import { ShareSettings } from './ShareSettings'
import { useGetShareQuery } from '@sketch/gql-types'

interface SharingModalBodyProps {
  share: ShareMinimalForSettingsModal
}

const LoadingLayout: React.FC = ({ children }) => {
  return (
    <>
      <Modal.Header as="div">
        <Flex justifyContent="space-between" alignItems="center">
          <SharingModalHeader>Share Document</SharingModalHeader>
        </Flex>
        <StyledSeparator />
      </Modal.Header>
      <Modal.Body>{children}</Modal.Body>
    </>
  )
}

const SharingModalBody = ({ share: { identifier } }: SharingModalBodyProps) => {
  const { data, loading, error } = useGetShareQuery({
    variables: { shortId: identifier },
  })

  if (loading) {
    return (
      <LoadingLayout>
        <Flex justifyContent="center">
          <LoadingPlaceholder size="64px" />
        </Flex>
      </LoadingLayout>
    )
  }

  const share = data?.share

  if (error || !share) {
    return (
      <LoadingLayout>
        <ErrorMessage.Generic />
      </LoadingLayout>
    )
  }

  return (
    <>
      <Modal.Header as="div">
        <Flex justifyContent="space-between" alignItems="center">
          <SharingModalHeader>
            Share Document
            <Breadcrumbs share={share} />
          </SharingModalHeader>
          <Breakpoint on="sm">
            <CopyButton
              size="40"
              showIcon={false}
              value={share.publicUrl}
              title="Copy Link"
            />
          </Breakpoint>
        </Flex>
        <StyledSeparator />
      </Modal.Header>

      <ModalBody>
        <ShareSettings share={share} />
      </ModalBody>
    </>
  )
}

interface SharingModalProps extends ModalInjectedProps {
  share: ShareMinimalForSettingsModal
}

const SharingModal = ({ hideModal, share }: SharingModalProps): JSX.Element => {
  return (
    <Modal onCancel={hideModal}>
      <SharingModalBody share={share} />

      <Modal.Footer>
        <Button onClick={hideModal}>Done</Button>
      </Modal.Footer>
    </Modal>
  )
}

export { SharingModal as default }
